import { z } from "zod";

/**
 * tRPC error-shape contract. Every typed API error carries a stable
 * `code` (`MVP.md` §4.9 / Phase 12 spec) plus an optional
 * `domainCode` originating from a domain `Illegal*Error` (e.g.,
 * `ID_EMPTY`, `REVOKED_AT_BEFORE_CREATED_AT`, `CONCURRENT_WRITE`).
 * The Phase 13 client branches on these without parsing messages.
 */
export const apiErrorCodeSchema = z.enum([
  "BAD_REQUEST",
  "NOT_FOUND",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "CONFLICT",
  "TIER_CAP_EXCEEDED",
  "PRECONDITION_FAILED",
  "INTERNAL_SERVER_ERROR",
]);
export type ApiErrorCode = z.infer<typeof apiErrorCodeSchema>;

export const apiErrorPayloadSchema = z.object({
  code: apiErrorCodeSchema,
  /**
   * The originating domain error code, when one applies. The client
   * uses this to render targeted micro-copy (e.g.,
   * `REVOKED_AT_BEFORE_CREATED_AT` is a programmer error and never
   * reaches a real user; `INSTALLATIONS_CONFLICT` deserves a
   * "two breakers want the same slot" message).
   */
  domainCode: z.string().min(1).optional(),
  /** Free-text message; never leak stack traces. */
  message: z.string().min(1),
  /**
   * Per-arm context payload (TIER_CAP_EXCEEDED carries the cap +
   * current + limit, CONFLICT carries the aggregate id +
   * expectedUpdatedAt + observedUpdatedAt, etc). Shape varies per
   * code; the client deserializes through the per-code schema in
   * `schemas/tier.ts` / `schemas/conflict.ts` (Phase 12d).
   */
  data: z.unknown().optional(),
});
export type ApiErrorPayload = z.infer<typeof apiErrorPayloadSchema>;
